import { Button } from "../ui/button";
import {
  Loader2, Upload, X, RotateCcw, ArrowLeftRight, Check,
} from "lucide-react";

/**
 * Recipe image section (cover photo + history gallery + A/B comparison).
 *
 * Pure presentational component — all state (comparingUrl, uploading, etc.)
 * is owned by RecipeDetailDialog and passed in via props.
 */
export function RecipeImageSection({
  imgSrc,
  localDish,
  isEditing,
  isBeverage,
  uploading,
  bevRegenLoading,
  comparingUrl,
  setComparingUrl,
  fileInputRef,
  handleUploadImage,
  handleRestoreImage,
  handleCancelBeverageRegen,
}) {
  return (
    <div className="space-y-3">
      <div className="relative">
        {imgSrc ? (
          <div className="relative rounded-lg overflow-hidden">
            <img
              src={imgSrc}
              alt={localDish?.name}
              className={isBeverage ? "w-full h-64 object-contain bg-card" : "w-full h-48 object-cover"}
              loading="lazy"
            />
            {isBeverage && bevRegenLoading && (
              <div
                className="absolute inset-0 bg-card/70 dark:bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-10"
                data-testid="bev-regen-overlay"
              >
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <p className="text-sm font-medium">Generando imagen con IA...</p>
                <p className="text-xs text-muted-foreground">Esto suele tardar 15–30 segundos</p>
                <Button size="sm" variant="outline" onClick={handleCancelBeverageRegen} data-testid="cancel-bev-regen-btn">
                  <X className="w-3 h-3 mr-1" />
                  Cancelar
                </Button>
              </div>
            )}
            {isEditing && (
              <div className="absolute bottom-2 right-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-image-btn"
                >
                  {uploading
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Subiendo...</>
                    : <><Upload className="w-4 h-4 mr-1" />Cambiar foto</>
                  }
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div
            className="w-full h-32 bg-muted rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={isEditing ? () => fileInputRef.current?.click() : undefined}
          >
            <div className="text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isEditing ? "Clic para subir una foto" : "Sin imagen"}
              </p>
            </div>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleUploadImage}
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
        />
      </div>

      {/* Image history gallery — only in edit mode */}
      {isEditing && localDish?.image_history?.length > 0 && (
        <div data-testid="image-history-gallery">
          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
            <RotateCcw className="w-3 h-3" />
            Historial ({localDish.image_history.length}/10) — clic para comparar
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {localDish.image_history.map((url, idx) => (
              <button
                key={idx}
                onClick={() => setComparingUrl(prev => prev === url ? null : url)}
                disabled={uploading}
                data-testid={`compare-image-${idx}`}
                title="Comparar con imagen actual"
                className={`shrink-0 relative group rounded overflow-hidden border-2 transition-all disabled:opacity-50 ${
                  comparingUrl === url
                    ? "border-primary shadow-md scale-105"
                    : "border-transparent hover:border-primary/60"
                }`}
              >
                <img
                  src={url}
                  alt={`Historial ${idx + 1}`}
                  className="w-16 h-12 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all">
                  <ArrowLeftRight className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {comparingUrl === url && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <ArrowLeftRight className="w-4 h-4 text-primary" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* A/B Comparison Panel */}
      {isEditing && comparingUrl && (
        <div
          className="rounded-xl overflow-hidden border border-primary/30 bg-muted/20 shadow-soft"
          data-testid="ab-comparison-panel"
        >
          <div className="grid grid-cols-2 divide-x divide-border/50">
            {/* Left: Current */}
            <div className="relative">
              <img
                src={imgSrc || ""}
                alt="Imagen actual"
                className="w-full h-44 object-cover"
              />
              <div className="absolute top-2 left-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-background/90 border border-border/60 text-foreground shadow-sm">
                  Actual
                </span>
              </div>
            </div>
            {/* Right: Alternative */}
            <div className="relative">
              <img
                src={comparingUrl}
                alt="Imagen alternativa"
                className="w-full h-44 object-cover"
              />
              <div className="absolute top-2 left-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-sm">
                  Alternativa
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 bg-background/60 border-t border-border/40">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <ArrowLeftRight className="w-3 h-3" />
              ¿Cuál prefieres?
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs h-7 px-3"
                onClick={() => setComparingUrl(null)}
                data-testid="ab-cancel-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Cancelar
              </Button>
              <Button
                size="sm"
                className="text-xs h-7 px-3 gap-1.5"
                onClick={() => { handleRestoreImage(comparingUrl); setComparingUrl(null); }}
                disabled={uploading}
                data-testid="ab-use-this-btn"
              >
                {uploading
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Check className="w-3 h-3" />
                }
                Usar esta
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
